import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Tooltip } from 'react-tooltip'
import SidebarLayout from 'components/SidebarLayout'
import { StyleUtil } from 'utils/StyleUtil'
import SearchInput from 'views/SearchInput'
import DropDownMenu, { DropDownItem } from 'views/DropDownMenu'
import CheckboxDropdown from 'views/CheckboxDropdown'
import embedSvg from 'images/embed.svg'
import copyLinkSvg from 'images/copy-link.svg'
import archIcon from 'images/content-finder-architecture.svg'
import topicsIcon from 'images/content-finder-topics.svg'
import quizzesIcon from 'images/menu-review.svg'
import imagesIcon from 'images/file-catalogue-images.svg'
import documentsIcon from 'images/file-catalogue-documents.svg'
import presentationsIcon from 'images/file-catalogue-presentations.svg'
import spreadsheetsIcon from 'images/file-catalogue-spreadsheets.svg'
import audioIcon from 'images/file-catalogue-audios.svg'
import videosIcon from 'images/file-catalogue-videos.svg'
import plusSvg from 'images/quiz-plus.svg'
import minusSvg from 'images/quiz-minus.svg'
import { useApi } from 'providers/ApiProvider'
import { useQuizApi } from 'providers/QuizApiProvider'
import { useArchitectureApi } from 'providers/ArchitectureApiProvider'
import Spinner from 'views/Spinner'
import { getDateText, getTimeText } from 'utils/StringUtil'
import { ToastUtil } from 'utils/ToastUtil'
import { resolveRelativeUrls } from 'utils/HtmlUtil'
import { getFamV4 } from '../mocks/MockTopicData'
import { Document } from 'apis/entities/document.entity'
import {
  ResultItem,
  TopicIndex,
  getDateStart,
  sortResultItems,
  buildTopicIndex,
  sortFlatTree,
  segmentFlatTree,
  flattenTree,
  getArchUrl,
} from './ContentFinderUtil'

const STORAGE_KEY = 'content-finder-recent-searches'
const MAX_RECENT = 5
const PAGE_SIZE = 10

const CONTENT_TYPE_ITEMS = [
  { id: '1', name: 'Architectures' },
  { id: '2', name: 'Topics' },
  { id: '3', name: 'Quizzes' },
  { id: '4', name: 'Images' },
  { id: '5', name: 'Documents' },
  { id: '6', name: 'Presentations' },
  { id: '7', name: 'Spreadsheets' },
  { id: '8', name: 'Audio' },
  { id: '9', name: 'Videos' },
]

const DATE_ITEMS = [
  { id: '0', name: 'Updated date', isLabel: true },
  { id: '1', name: 'Last 7 days' },
  { id: '2', name: 'Last 30 days' },
  { id: '3', name: 'Last 6 months' },
  { id: '4', name: 'All dates' },
]

const CONTENT_TYPE_ICONS: Record<string, string> = {
  '1': archIcon,
  '2': topicsIcon,
  '3': quizzesIcon,
  '4': imagesIcon,
  '5': documentsIcon,
  '6': presentationsIcon,
  '7': spreadsheetsIcon,
  '8': audioIcon,
  '9': videosIcon,
}

const SORT_ITEMS = [
  { id: '0', name: 'Sort by', isLabel: true },
  { id: '1', name: 'Newest first' },
  { id: '2', name: 'Oldest first' },
  { id: '3', name: 'A → Z' },
  { id: '4', name: 'Z → A' },
  { id: '5', name: 'Most relevant' },
]

type ContentTypeResult = {
  typeId: string
  typeName: string
  items: ResultItem[]
  totalItems: number
  topicIndex?: TopicIndex
}

type RecentSearch = {
  text: string
  contentTypes: string[]
  dateId: string
  sortId: string
  searchedAt?: string
}

function loadRecentSearches(): RecentSearch[] {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]')
  } catch {
    return []
  }
}

function saveRecentSearches(searches: RecentSearch[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(searches))
}

function itemsWithDefault<T extends { id: string; default?: boolean }>(
  items: T[],
  defaultId: string,
): T[] {
  return items.map((item) => ({ ...item, default: item.id === defaultId }))
}

function makeLxFilter(
  type:
    | 'documents'
    | 'presentations'
    | 'spreadsheets'
    | 'audio'
    | 'videos'
    | 'other',
) {
  const fileTypeFilter = {
    documents: false,
    presentations: false,
    spreadsheets: false,
    audio: false,
    videos: false,
    other: false,
    [type]: true,
  }
  return `published=1&fileTypeFilter=${JSON.stringify(fileTypeFilter)}`
}

function ResultItemRow({
  item,
  num,
  nChildren,
  isExpanded,
  onToggle,
}: {
  item: ResultItem
  num: number | null
  nChildren: number
  isExpanded: boolean
  onToggle: (id: string) => void
}) {
  const { getDocumentHtml } = useApi()
  return (
    <li className="flex items-center gap-4 py-1 text-[14px]">
      <span className="text-gray-400 text-[14px] shrink-0 w-[20px]">
        {num !== null ? `${num}.` : ''}
      </span>
      <div
        className={`flex items-center gap-2 min-w-0 flex-1 ${
          item.subtitle ? 'max-w-[400px]' : ''
        }`}
        style={{ paddingLeft: `${(item.level || 0) * 20}px` }}
      >
        {item.url ? (
          <a
            href={item.url}
            target="_blank"
            rel="noreferrer"
            className="text-secondaryIndigo underline hover:opacity-80 truncate font-semibold pr-[20px]"
            data-tooltip-id={`tooltip-cf-name-${item.id}`}
          >
            {item.name}
          </a>
        ) : (
          <span
            className="text-gray-700 truncate font-semibold pr-[20px]"
            data-tooltip-id={`tooltip-cf-name-${item.id}`}
          >
            {item.name}
          </span>
        )}
        <Tooltip
          id={`tooltip-cf-name-${item.id}`}
          className="mentem-tooltip"
          style={{
            backgroundColor: '#fff',
            color: '#000',
            borderRadius: '6px',
          }}
          place="top"
          noArrow={true}
        >
          {item.name}
        </Tooltip>
        {nChildren > 0 && (
          <button
            data-testid={`expand-collapse-${item.id}`}
            className="flex items-center gap-1 text-black text-[14px] shrink-0 ml-auto"
            onClick={() => onToggle(item.id)}
          >
            {nChildren}
            <img
              src={isExpanded ? minusSvg : plusSvg}
              alt={isExpanded ? 'collapse' : 'expand'}
              style={{
                width: 13,
                paddingTop: isExpanded ? '4px' : '0',
                display: 'inline',
              }}
            />
          </button>
        )}
      </div>
      {item.subtitle && (
        <div className="min-w-0 flex-1">
          <span
            className="text-[14px] font-semibold truncate block mr-[20px] max-w-[276px]"
            style={{ fontFamily: 'Roboto', color: '#000000' }}
            data-tooltip-id={`tooltip-cf-subtitle-${item.id}`}
          >
            {item.subtitle}
          </span>
          <Tooltip
            id={`tooltip-cf-subtitle-${item.id}`}
            className="mentem-tooltip"
            style={{
              backgroundColor: '#fff',
              color: '#000',
              borderRadius: '6px',
            }}
            place="top"
            noArrow={true}
          >
            {item.subtitle}
          </Tooltip>
        </div>
      )}
      {item.createdAt && (
        <span className="text-[#8A68C8] text-[14px] shrink-0 whitespace-nowrap w-[210px]">
          {`Updated ${getDateText(item.createdAt)} ${getTimeText(
            item.createdAt,
          )}`}
        </span>
      )}
      <div className="w-[24px] flex justify-center">
        {item.url && (
          <>
            <button
              data-tooltip-id={`tooltip-cf-link-${item.id}`}
              onClick={() => {
                navigator.clipboard.writeText(item.url!)
                ToastUtil.success('Link copied to clipboard')
              }}
            >
              <img
                src={copyLinkSvg}
                alt="copy link"
                className="w-4 h-4 brightness-0 hover:opacity-60"
              />
            </button>
            <Tooltip
              id={`tooltip-cf-link-${item.id}`}
              className="mentem-tooltip"
              style={{
                backgroundColor: '#fff',
                color: '#000',
                borderRadius: '6px',
              }}
              place="top"
              noArrow={true}
            >
              Copy link
            </Tooltip>
          </>
        )}
      </div>
      <div className="w-[24px] flex justify-center">
        {item.url && item.embedSupported && (
          <>
            <button
              data-tooltip-id={`tooltip-cf-embed-${item.id}`}
              onClick={() => {
                const code = `<iframe width="100%" height="1200" src="${item.url}" frameborder="0" allow="web-share;fullscreen;"></iframe>`
                navigator.clipboard.writeText(code)
                ToastUtil.success('Embed code copied to clipboard')
              }}
            >
              <img
                src={embedSvg}
                alt="embed"
                className="w-4 h-4 brightness-0 hover:opacity-60"
              />
            </button>
            <Tooltip
              id={`tooltip-cf-embed-${item.id}`}
              className="mentem-tooltip"
              style={{
                backgroundColor: '#fff',
                color: '#000',
                borderRadius: '6px',
              }}
              place="top"
              noArrow={true}
            >
              Copy embed code
            </Tooltip>
          </>
        )}
      </div>
      <div className="w-[24px] flex justify-center">
        {item.url && item.embedSupported && (
          <>
            <button
              data-tooltip-id={`tooltip-cf-html-${item.id}`}
              onClick={async () => {
                try {
                  const html = await getDocumentHtml(item.id)
                  const absoluteHtml = resolveRelativeUrls(html, item.url!)
                  await navigator.clipboard.writeText(absoluteHtml)
                  ToastUtil.success('HTML copied to clipboard')
                } catch (err) {
                  console.error(err)
                  ToastUtil.error('Failed to copy HTML')
                }
              }}
            >
              <img
                src={embedSvg}
                alt="copy html"
                className="w-4 h-4 brightness-0 hover:opacity-60"
              />
            </button>
            <Tooltip
              id={`tooltip-cf-html-${item.id}`}
              className="mentem-tooltip"
              style={{
                backgroundColor: '#fff',
                color: '#000',
                borderRadius: '6px',
              }}
              place="top"
              noArrow={true}
            >
              Copy HTML
            </Tooltip>
          </>
        )}
      </div>
    </li>
  )
}

function ContentFinderPage() {
  const {
    getLxFiles,
    getImagesBySearch,
    getDocumentsV2,
    getRecentlyCreatedFiles,
  } = useApi()
  const { searchContentFinder: searchQuizzes, getRecentlyUpdatedQuizzes } =
    useQuizApi()
  const {
    searchProgramsCourses,
    getRecentlyUpdatedArchitectures,
    getRecentlyUpdatedTopics,
  } = useArchitectureApi()

  const searchText = useRef('')
  const [contentTypes, setContentTypes] = useState<string[]>([])
  const [dateId, setDateId] = useState('4')
  const [sortId, setSortId] = useState('5')
  const [recentSearches, setRecentSearches] =
    useState<RecentSearch[]>(loadRecentSearches)
  const [mountKey, setMountKey] = useState(0)
  const [isSearching, setIsSearching] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [displayedTerm, setDisplayedTerm] = useState('')
  const [expandedTopicIds, setExpandedTopicIds] = useState<string[]>([])
  const [rawResults, setRawResults] = useState<ContentTypeResult[]>([])
  const [visibleCounts, setVisibleCounts] = useState<Record<string, number>>({})
  const [recentlyUpdated, setRecentlyUpdated] = useState<
    {
      clientId: string
      client: string
      programId: string
      courseId: string
      course: string
      lastUpdated: string
    }[]
  >([])
  const [recentlyUpdatedTopics, setRecentlyUpdatedTopics] = useState<
    { id: string; name: string; createdAt: string }[]
  >([])
  const [recentlyUpdatedQuizzes, setRecentlyUpdatedQuizzes] = useState<
    {
      id: string
      name: string
      updatedAt: string
      updatedByUserName: string | null
      updatedByFirstName: string | null
    }[]
  >([])
  const [recentFiles, setRecentFiles] = useState<
    Record<
      string,
      { id: string; name: string; createdAt: string; fileUrl: string }[]
    >
  >({})

  useEffect(() => {
    getRecentlyUpdatedArchitectures(5)
      .then(setRecentlyUpdated)
      .catch(() => {})
    getRecentlyUpdatedTopics(5)
      .then(setRecentlyUpdatedTopics)
      .catch(() => {})
    getRecentlyUpdatedQuizzes(5)
      .then(setRecentlyUpdatedQuizzes)
      .catch(() => {})
    getRecentlyCreatedFiles(5)
      .then(setRecentFiles)
      .catch(() => {})
  }, [])

  const fileIconMap: Record<string, string> = {
    documents: documentsIcon,
    presentations: presentationsIcon,
    spreadsheets: spreadsheetsIcon,
    audio: audioIcon,
    videos: videosIcon,
  }

  const allRecentlyUpdated = useMemo(() => {
    type UnifiedItem = {
      name: string
      date: string
      icon: string
      url?: string
      type: string
    }
    const fileTypeLabel: Record<string, string> = {
      documents: 'Document',
      presentations: 'Presentation',
      spreadsheets: 'Spreadsheet',
      audio: 'Audio',
      videos: 'Video',
    }
    const items: UnifiedItem[] = [
      ...recentlyUpdated.map((x) => ({
        name: x.course,
        date: x.lastUpdated,
        icon: archIcon,
        url: getArchUrl(x.clientId, x.programId) || undefined,
        type: 'Architecture',
      })),
      ...recentlyUpdatedTopics.map((x) => ({
        name: x.name,
        date: x.createdAt,
        icon: topicsIcon,
        type: 'Topic',
      })),
      ...recentlyUpdatedQuizzes.map((x) => ({
        name: x.name,
        date: x.updatedAt,
        icon: quizzesIcon,
        url: `/quizzes/${x.id}`,
        type: 'Quiz',
      })),
      ...Object.entries(recentFiles).flatMap(([key, files]) =>
        files.map((f) => ({
          name: f.name,
          date: f.createdAt,
          icon: fileIconMap[key],
          url: f.fileUrl,
          type: fileTypeLabel[key] ?? key,
        })),
      ),
    ]
    return items
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 5)
  }, [
    recentlyUpdated,
    recentlyUpdatedTopics,
    recentlyUpdatedQuizzes,
    recentFiles,
  ])

  const results = useMemo(
    () =>
      rawResults.map((group) => {
        const items =
          group.typeId === '2'
            ? sortFlatTree(group.items, sortId)
            : sortResultItems(group.items, sortId)
        return {
          ...group,
          items,
          topicIndex: group.typeId === '2' ? buildTopicIndex(items) : undefined,
        }
      }),
    [rawResults, sortId],
  )

  const expandedSet = useMemo(
    () => new Set(expandedTopicIds),
    [expandedTopicIds],
  )

  const toggleTopic = useCallback((id: string) => {
    setExpandedTopicIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    )
  }, [])

  const runSearch = async (
    term: string,
    selectedTypes: string[],
    selectedDateId: string,
  ) => {
    const typesToSearch =
      selectedTypes.length > 0
        ? selectedTypes
        : CONTENT_TYPE_ITEMS.map((i) => i.id)
    const dateStart = getDateStart(selectedDateId)

    setIsSearching(true)
    setHasSearched(true)
    setDisplayedTerm(term)
    setExpandedTopicIds([])
    setRawResults([])
    setVisibleCounts({})

    const needsArch = typesToSearch.includes('1')
    const needsTopics = typesToSearch.includes('2')
    const lxTypeMap: Record<
      string,
      | 'documents'
      | 'presentations'
      | 'spreadsheets'
      | 'audio'
      | 'videos'
      | 'other'
    > = {
      '5': 'documents',
      '6': 'presentations',
      '7': 'spreadsheets',
      '8': 'audio',
      '9': 'videos',
    }

    const tasks: Promise<ContentTypeResult | ContentTypeResult[] | null>[] = []

    if (needsArch) {
      tasks.push(
        searchProgramsCourses(term).then((results) => {
          const archItems: ResultItem[] = results.map((x: any) => ({
            id: x.id,
            name: x.name,
            subtitle: x.matchedOn,
            score: x.similarity,
            url: getArchUrl(x.clientId, x.programId),
            createdAt: x.lastUpdated ?? undefined,
          }))
          return [
            {
              typeId: '1',
              typeName: 'Architectures',
              items: archItems,
              totalItems: archItems.length,
            },
          ] as any
        }),
      )
    }

    if (needsTopics) {
      tasks.push(
        getDocumentsV2(
          'skunkworks=1&published=1&clientSpecific=0&isTopic=1',
          term,
          dateStart,
          undefined,
          100,
          1,
        ).then((result) => {
          const docs: Document[] = (result?.items || []).map(
            (doc: Document) => ({
              ...doc,
              _TEMP_PARENT_ID: doc.parentIds[0] || null,
            }),
          )
          const { masterDocs } = getFamV4(docs, false)
          const topicItems = masterDocs
            .flatMap((doc) => flattenTree(doc))
            .map((i) => ({ ...i, subtitle: undefined }))
          return {
            typeId: '2',
            typeName: 'Topics',
            items: topicItems,
            totalItems: result?.totalItems ?? topicItems.length,
          }
        }),
      )
    }

    if (typesToSearch.includes('3')) {
      tasks.push(
        searchQuizzes(term).then((quizzes) => {
          const items = (quizzes || []).map((q) => ({
            id: q.id,
            name: q.name,
            createdAt: q.createdAt,
            url: `/quizzes/${q.id}`,
          }))
          return {
            typeId: '3',
            typeName: 'Quizzes',
            items,
            totalItems: items.length,
          }
        }),
      )
    }

    if (typesToSearch.includes('4')) {
      tasks.push(
        getImagesBySearch(
          1,
          100,
          undefined,
          undefined,
          ['_ALL_'],
          undefined,
          term,
          true,
        ).then(([images]) => {
          const items = (images || []).map((img: any) => ({
            id: img.id,
            name: img.name,
            url: img.url,
            score: img.similarity != null ? img.similarity / 3 : undefined,
            imageScore:
              img.image_similarity != null ? img.image_similarity : undefined,
            textScore:
              img.text_similarity != null ? img.text_similarity : undefined,
            category: img.category_name ?? undefined,
            createdAt: img.createdAt ?? undefined,
          }))
          return {
            typeId: '4',
            typeName: 'Images',
            items,
            totalItems: items.length,
          }
        }),
      )
    }

    for (const [typeId, lxType] of Object.entries(lxTypeMap)) {
      if (typesToSearch.includes(typeId)) {
        const typeName = CONTENT_TYPE_ITEMS.find((i) => i.id === typeId)!.name
        tasks.push(
          getLxFiles(makeLxFilter(lxType), term, dateStart).then((files) => {
            const items = (files || []).map((f: any) => ({
              id: f.id,
              name: f.name,
              url: f.fileUrl,
              createdAt: f.createdAt,
              score: f.similarity != null ? f.similarity : undefined,
            }))
            return { typeId, typeName, items, totalItems: items.length }
          }),
        )
      }
    }

    const settled = await Promise.allSettled(tasks)
    const allResults: ContentTypeResult[] = []
    let failedCount = 0
    for (const result of settled) {
      if (result.status === 'rejected') {
        failedCount++
      } else if (result.value) {
        if (Array.isArray(result.value)) {
          allResults.push(...result.value)
        } else {
          allResults.push(result.value)
        }
      }
    }
    if (failedCount > 0) {
      ToastUtil.error(
        `${failedCount} content type${
          failedCount > 1 ? 's' : ''
        } failed to load`,
      )
    }

    allResults.sort((a, b) => Number(a.typeId) - Number(b.typeId))
    setRawResults(allResults)
    setIsSearching(false)
  }

  const saveAndSearch = (
    term: string,
    selectedTypes: string[],
    selectedDateId: string,
    selectedSortId: string,
  ) => {
    const entry: RecentSearch = {
      text: term,
      contentTypes: selectedTypes,
      dateId: selectedDateId,
      sortId: selectedSortId,
      searchedAt: new Date().toISOString(),
    }
    setRecentSearches((prev) => {
      const deduped = [entry, ...prev.filter((s) => s.text !== term)].slice(
        0,
        MAX_RECENT,
      )
      saveRecentSearches(deduped)
      return deduped
    })
    runSearch(term, selectedTypes, selectedDateId)
  }

  const onEnter = () => {
    const term = searchText.current.trim()
    if (!term) return
    saveAndSearch(term, contentTypes, dateId, sortId)
  }

  return (
    <SidebarLayout>
      <div className={StyleUtil.container}>
        <p className={StyleUtil.headline}>
          Find what you're looking for in the LX Engine content catalogue
        </p>
        <div className="flex items-start justify-between pt-8">
          <div className="w-[456px]">
            <SearchInput
              key={mountKey}
              initialValue={searchText.current}
              onInputChanged={(value) => {
                searchText.current = value
                if (value === '') {
                  setHasSearched(false)
                  setRawResults([])
                  setDisplayedTerm('')
                }
              }}
              onEnter={onEnter}
              isSearching={isSearching}
              placeholder="What are you looking for today?"
              hideButton={true}
            />
          </div>
          <div className="flex gap-3">
            <CheckboxDropdown
              key={mountKey}
              label="Content type"
              items={CONTENT_TYPE_ITEMS}
              initialSelectedIds={contentTypes}
              onSelected={(ids) => {
                setContentTypes(ids)
                const term = searchText.current.trim()
                if (term) saveAndSearch(term, ids, dateId, sortId)
              }}
            />
            <DropDownMenu
              key={`date-${mountKey}`}
              items={itemsWithDefault(DATE_ITEMS, dateId)}
              onSelected={(item: DropDownItem) => {
                setDateId(item.id)
                const term = searchText.current.trim()
                if (term) saveAndSearch(term, contentTypes, item.id, sortId)
              }}
            />
            <DropDownMenu
              key={`sort-${mountKey}`}
              items={itemsWithDefault(SORT_ITEMS, sortId)}
              onSelected={(item: DropDownItem) => setSortId(item.id)}
            />
          </div>
        </div>

        {hasSearched && (
          <p
            className={`${StyleUtil.headlineSmall} pt-8`}
            style={{
              fontSize: 18,
              fontWeight: 400,
              lineHeight: '22px',
              fontFeatureSettings: "'liga' off, 'clig' off",
            }}
          >
            Results for "{displayedTerm}"
          </p>
        )}
        {isSearching && <Spinner />}
        {hasSearched && !isSearching && results.length === 0 && (
          <p className="pt-4 text-[14px] text-black">No results found.</p>
        )}
        {!isSearching &&
          results.map((group) => {
            const visible = visibleCounts[group.typeId] ?? PAGE_SIZE
            let visibleItems: ResultItem[]
            let totalCount: number
            if (group.typeId === '2') {
              const segments = segmentFlatTree(group.items)
              totalCount = segments.length
              visibleItems = segments.slice(0, visible).flat()
            } else {
              totalCount = group.items.length
              visibleItems = group.items.slice(0, visible)
            }
            const remaining = totalCount - visible
            return (
              <div key={group.typeId} className="pt-6">
                <div className="flex items-center gap-2 pb-2">
                  <img
                    src={CONTENT_TYPE_ICONS[group.typeId]}
                    alt={group.typeName}
                    className="w-4 h-4 object-contain"
                  />
                  <p
                    style={{
                      fontFamily: 'Clancy',
                      fontSize: 16,
                      fontWeight: 400,
                    }}
                  >
                    {group.typeName}
                  </p>
                </div>
                {group.items.length === 0 ? (
                  <p className="text-[14px] text-black">No results</p>
                ) : (
                  <>
                    <ul className="list-none">
                      {(() => {
                        const { childCounts = {}, itemParentId = {} } =
                          group.topicIndex ?? {}
                        let parentCount = 0
                        return visibleItems
                          .filter(
                            (item) =>
                              !item.level ||
                              expandedSet.has(itemParentId[item.id] ?? ''),
                          )
                          .map((item) => {
                            if (!item.level) parentCount++
                            return (
                              <ResultItemRow
                                key={item.id}
                                item={item}
                                num={null}
                                nChildren={childCounts[item.id] ?? 0}
                                isExpanded={expandedSet.has(item.id)}
                                onToggle={toggleTopic}
                              />
                            )
                          })
                      })()}
                    </ul>
                    <div className="flex flex-col ml-[36px] mt-2">
                      {remaining > 0 && (
                        <button
                          className="text-[14px] text-black font-semibold leading-normal hover:opacity-60 text-left"
                          style={{
                            fontFamily: 'Roboto',
                            fontFeatureSettings: "'liga' off, 'clig' off",
                          }}
                          onClick={() =>
                            setVisibleCounts((prev) => ({
                              ...prev,
                              [group.typeId]: visible + PAGE_SIZE,
                            }))
                          }
                        >
                          Show more +
                        </button>
                      )}
                      {visible > PAGE_SIZE && (
                        <button
                          className="text-[14px] text-black font-semibold leading-normal hover:opacity-60 text-left"
                          style={{
                            fontFamily: 'Roboto',
                            fontFeatureSettings: "'liga' off, 'clig' off",
                          }}
                          onClick={() =>
                            setVisibleCounts((prev) => ({
                              ...prev,
                              [group.typeId]: visible - PAGE_SIZE,
                            }))
                          }
                        >
                          Show less -
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            )
          })}

        {!hasSearched && !isSearching && (
          <p
            className={`${StyleUtil.headlineSmall} pt-8`}
            style={{ fontFamily: 'Clancy', fontSize: 18, fontWeight: 400 }}
          >
            Recent searches
          </p>
        )}
        {!hasSearched && !isSearching && recentSearches.length > 0 && (
          <ul className="pt-6">
            {recentSearches.map((entry) => (
              <li key={entry.text} className="flex items-center gap-4 p-3">
                <button
                  className="text-[14px] text-secondaryIndigo underline hover:opacity-80"
                  onClick={() => {
                    const now = new Date().toISOString()
                    searchText.current = entry.text
                    setContentTypes(entry.contentTypes)
                    setDateId(entry.dateId)
                    setSortId(entry.sortId)
                    setMountKey((k) => k + 1)
                    setRecentSearches((prev) => {
                      const updated = prev.map((s) =>
                        s.text === entry.text ? { ...s, searchedAt: now } : s,
                      )
                      saveRecentSearches(updated)
                      return updated
                    })
                    runSearch(entry.text, entry.contentTypes, entry.dateId)
                  }}
                >
                  {entry.text}
                </button>
              </li>
            ))}
          </ul>
        )}

        {!hasSearched && !isSearching && allRecentlyUpdated.length > 0 && (
          <div className="pt-8">
            <p
              className={StyleUtil.headlineSmall}
              style={{ fontFamily: 'Clancy', fontSize: 18, fontWeight: 400 }}
            >
              Recently updated content
            </p>
            <ul className="pt-4">
              {allRecentlyUpdated.map((item, i) => (
                <li key={i} className="flex items-start gap-4 py-1">
                  <div className="min-w-0 flex-1">
                    {item.url ? (
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-secondaryIndigo underline hover:opacity-80 truncate block text-[14px]"
                      >
                        {item.name}
                      </a>
                    ) : (
                      <span className="text-gray-700 truncate block text-[14px]">
                        {item.name}
                      </span>
                    )}
                    <div className="flex items-center gap-1 mt-0.5">
                      <img
                        src={item.icon}
                        alt=""
                        className="w-3 h-3 shrink-0"
                      />
                      <span className="text-black text-[14px]">
                        {item.type}
                      </span>
                    </div>
                  </div>
                  <span className="text-[#8A68C8] text-[14px] shrink-0 whitespace-nowrap w-[210px] pt-px">
                    {`Updated ${getDateText(item.date)} ${getTimeText(
                      item.date,
                    )}`}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* recently updated by type — hidden in favour of unified list above
        {!hasSearched && !isSearching && (
          <div className="pt-8">
            <p className={StyleUtil.headlineSmall}>Recently updated content</p>

            {recentlyUpdated.length > 0 && (
              <div className="pt-6">
                <div className="flex items-center gap-2 pb-1">
                  <img src={archIcon} alt="Architectures" className="w-4 h-4" />
                  <p className="text-[14px] font-semibold text-gray-500">Architectures</p>
                </div>
                <ul>
                  {recentlyUpdated.map((item) => {
                    const url = getArchUrl(item.clientId, item.programId)
                    return (
                      <li key={item.courseId} className="flex items-center gap-4 py-1 text-[14px]">
                        <span className="text-gray-400 text-[14px] shrink-0 w-[20px]" />
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          {url ? (
                            <a href={url} className="text-secondaryIndigo underline hover:opacity-80 truncate">
                              {item.course}
                            </a>
                          ) : (
                            <span className="text-gray-700 truncate">{item.course}</span>
                          )}
                          <span className="text-gray-400 text-[14px] shrink-0">{item.client}</span>
                        </div>
                        <span className="text-gray-400 text-[14px] shrink-0 text-right whitespace-nowrap">
                          {`Updated ${getDateText(item.lastUpdated)} ${getTimeText(item.lastUpdated)}`}
                        </span>
                      </li>
                    )
                  })}
                </ul>
              </div>
            )}

            {recentlyUpdatedTopics.length > 0 && (
              <div className="pt-6">
                <div className="flex items-center gap-2 pb-1">
                  <img src={topicsIcon} alt="Topics" className="w-4 h-4" />
                  <p className="text-[14px] font-semibold text-gray-500">Topics</p>
                </div>
                <ul>
                  {recentlyUpdatedTopics.map((topic) => (
                    <li key={topic.id} className="flex items-center gap-4 py-1 text-[14px]">
                      <span className="text-gray-400 text-[14px] shrink-0 w-[20px]" />
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <span className="text-gray-700 truncate">{topic.name}</span>
                      </div>
                      <span className="text-gray-400 text-[14px] shrink-0 text-right whitespace-nowrap">
                        {`Updated ${getDateText(topic.createdAt)} ${getTimeText(topic.createdAt)}`}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {recentlyUpdatedQuizzes.length > 0 && (
              <div className="pt-6">
                <div className="flex items-center gap-2 pb-1">
                  <img src={quizzesIcon} alt="Quizzes" className="w-4 h-4" />
                  <p className="text-[14px] font-semibold text-gray-500">Quizzes</p>
                </div>
                <ul>
                  {recentlyUpdatedQuizzes.map((quiz) => (
                    <li key={quiz.id} className="flex items-center gap-4 py-1 text-[14px]">
                      <span className="text-gray-400 text-[14px] shrink-0 w-[20px]" />
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <a href={`/quizzes/${quiz.id}`} className="text-secondaryIndigo underline hover:opacity-80 truncate">
                          {quiz.name}
                        </a>
                        {(quiz.updatedByFirstName || quiz.updatedByUserName) && (
                          <span className="text-gray-400 text-[14px] shrink-0">{quiz.updatedByFirstName || quiz.updatedByUserName}</span>
                        )}
                      </div>
                      <span className="text-gray-400 text-[14px] shrink-0 text-right whitespace-nowrap">
                        {`Updated ${getDateText(quiz.updatedAt)} ${getTimeText(quiz.updatedAt)}`}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {([
              { key: 'documents',     label: 'Documents',     icon: documentsIcon },
              { key: 'presentations', label: 'Presentations', icon: presentationsIcon },
              { key: 'spreadsheets',  label: 'Spreadsheets',  icon: spreadsheetsIcon },
              { key: 'audio',         label: 'Audio',         icon: audioIcon },
              { key: 'videos',        label: 'Videos',        icon: videosIcon },
            ] as const).map(({ key, label, icon }) => {
              const items = recentFiles[key]
              if (!items?.length) return null
              return (
                <div key={key} className="pt-6">
                  <div className="flex items-center gap-2 pb-1">
                    <img src={icon} alt={label} className="w-4 h-4" />
                    <p className="text-[14px] font-semibold text-gray-500">{label}</p>
                  </div>
                  <ul>
                    {items.map((file) => (
                      <li key={file.id} className="flex items-center gap-4 py-1 text-[14px]">
                        <span className="text-gray-400 text-[14px] shrink-0 w-[20px]" />
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <a href={file.fileUrl} target="_blank" rel="noreferrer" className="text-secondaryIndigo underline hover:opacity-80 truncate">
                            {file.name}
                          </a>
                        </div>
                        <span className="text-gray-400 text-[14px] shrink-0 text-right whitespace-nowrap">
                          {`Updated ${getDateText(file.createdAt)} ${getTimeText(file.createdAt)}`}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )
            })}
          </div>
        )}
        */}
      </div>
    </SidebarLayout>
  )
}

export default ContentFinderPage
